/**
 * Session state for the whole app.
 *
 * One request to GET /api/auth/session on mount, then kept in memory. The
 * session itself lives in an HttpOnly cookie the JavaScript cannot read
 * (Architecture §6.3), so this context holds only what the UI needs to
 * display: which studio, who is using the system, and whether the admin
 * window is currently open.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '../api/client.js';

export interface SessionStudio {
  id: string;
  name: string;
  paletteKey: string;
  templateKey: string;
}

export interface SessionInstructor {
  id: string;
  name: string;
}

export interface SessionData {
  studio: SessionStudio;
  /**
   * 'owner' reaches every student in the studio and manages instructors;
   * 'instructor' reaches their own students only.
   */
  role: 'owner' | 'instructor';
  activeInstructor: SessionInstructor | null;
  /** True when this session bought the studio-wide view with the password. */
  managerMode: boolean;
  adminReauthValidUntil: string | null;
  /** True when the picker no longer asks for a second password to switch. */
  skipProfilePassword: boolean;
}

type Status = 'loading' | 'authenticated' | 'anonymous';

interface SessionContextValue {
  status: Status;
  session: SessionData | null;
  /** Re-reads the session from the server. */
  refresh: () => Promise<void>;
  setActiveInstructor: (instructor: SessionInstructor) => void;
  markAdminReauthenticated: (validUntil: string | null) => void;
  signOut: () => Promise<void>;
  /** True while the ten-minute admin window is still open. */
  hasAdminAccess: () => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<SessionData | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<SessionData>('/api/auth/session');
      setSession(data);
      setStatus('authenticated');
    } catch (err) {
      // Any auth failure is simply "not signed in" from the UI's point of
      // view; the login screen is the answer to all of them.
      if (err instanceof ApiError) {
        setSession(null);
        setStatus('anonymous');
        return;
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    /*
     * The render page is opened by a browser with no cookie, and asking after
     * a session there costs a guaranteed 401 on every generated report. Worse,
     * it never settles: the renderer waits for the page to go quiet, and a
     * request that always fails keeps it talking.
     */
    if (window.location.pathname.startsWith('/rapor-cikti/')) {
      setStatus('anonymous');
      return;
    }
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      session,
      refresh,
      setActiveInstructor: (instructor) =>
        setSession((current) =>
          current ? { ...current, activeInstructor: instructor } : current,
        ),
      markAdminReauthenticated: (validUntil) =>
        setSession((current) =>
          current ? { ...current, adminReauthValidUntil: validUntil } : current,
        ),
      signOut: async () => {
        try {
          await api.post('/api/auth/logout');
        } finally {
          // Even if the request fails, drop local state — leaving the UI
          // looking signed in after the user asked to leave is worse than a
          // stale server session that expires on its own.
          setSession(null);
          setStatus('anonymous');
        }
      },
      hasAdminAccess: () => {
        const until = session?.adminReauthValidUntil;
        return until ? Date.parse(until) > Date.now() : false;
      },
    }),
    [status, session, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
